import type { Bundle } from '../types'

/** Détection pubs : annonceurs, bannières, campagnes, requêtes surveillées. */
export const ads = {
  // Pays dans une phrase. Le français choisit la préposition selon le genre du
  // pays ; l'anglais n'a qu'un « in ». La clé est donc traduite, pas calculée.
  'ads.inCountryM': { fr: 'au {country}', en: 'in {country}' },
  'ads.inCountryF': { fr: 'en {country}', en: 'in {country}' },
  'ads.inScope': { fr: 'dans votre périmètre', en: 'within your scope' },

  // Vue d'ensemble
  'ads.overviewTitle': { fr: 'Publicité {where}', en: 'Advertising {where}' },
  'ads.overviewDesc': {
    fr: 'Annonceurs vus sur Google, Meta et les bannières des sites surveillés.',
    en: 'Advertisers seen on Google, Meta and the banners of monitored websites.',
  },
  'ads.overviewDescScanned': {
    fr: 'Annonceurs vus sur Google, Meta et les bannières des sites surveillés. Dernier scan le {date}.',
    en: 'Advertisers seen on Google, Meta and the banners of monitored websites. Last scan on {date}.',
  },
  'ads.runScan': { fr: 'Lancer un scan', en: 'Run a scan' },
  'ads.noSourceConfigured': {
    fr: 'Aucune source publicitaire n’est configurée : les scans ne relèvent que les bannières. La collecte Meta attend une clé Apify, et Google une clé SerpAPI.',
    en: 'No advertising source is configured: scans pick up banners only. Meta collection needs an Apify key, Google a SerpAPI key.',
  },
  'ads.noMetaConfigured': {
    fr: 'La collecte Meta passe par Apify, qui n’est pas configuré : seules les annonces Google et les bannières sont relevées.',
    en: 'Meta collection goes through Apify, which is not configured: only Google ads and banners are collected.',
  },
  'ads.noGoogleConfigured': {
    fr: 'Google Ads n’est pas configuré : seules les annonces Meta et les bannières sont relevées.',
    en: 'Google Ads is not configured: only Meta ads and banners are collected.',
  },
  'ads.kpiAdvertisers': { fr: 'Annonceurs', en: 'Advertisers' },
  'ads.kpiAdvertisersSub': {
    fr: '+{n} nouvelles entrées sur 30 jours · {organic} résultats naturels à part',
    en: '+{n} new entries over 30 days · {organic} organic results besides',
  },
  'ads.kpiToReview': { fr: 'À vérifier', en: 'To check' },
  'ads.kpiToReviewSub': { fr: 'nouveaux, sans décision', en: 'new, no decision yet' },
  'ads.kpiUnregistered': { fr: 'Non enregistrés fiscalement', en: 'Not registered for tax' },
  'ads.kpiUnregisteredSub': { fr: '{percent} des entrées', en: '{percent} of entries' },
  'ads.kpiSensitive': { fr: 'Activités sensibles', en: 'Sensitive activities' },
  'ads.kpiSensitiveSub': { fr: "jeux d'argent", en: 'gambling' },
  'ads.byChannel': { fr: 'Par canal', en: 'By channel' },
  'ads.byChannelSub': { fr: "Où l'annonceur a été vu", en: 'Where the advertiser was seen' },
  'ads.noAdvertiserYet': { fr: "Aucun annonceur relevé pour l'instant.", en: 'No advertiser collected yet.' },
  'ads.topSectors': { fr: 'Secteurs les plus présents', en: 'Most present sectors' },
  'ads.noSectorYet': { fr: "Aucun secteur détecté pour l'instant.", en: 'No sector detected yet.' },
  'ads.toReviewBlock': { fr: 'Annonceurs à vérifier', en: 'Advertisers to check' },
  'ads.toReviewBlockSub': { fr: 'Sans décision, les plus récemment vus', en: 'No decision yet, most recently seen' },
  'ads.seeAllAdvertisers': { fr: 'Voir les {n} annonceurs', en: 'See all {n} advertisers' },
  'ads.noneToReview': { fr: 'Aucun annonceur en attente de vérification.', en: 'No advertiser awaiting a check.' },
  'ads.check': { fr: 'Vérifier', en: 'Check' },

  // Canaux et types
  'ads.platform.GOOGLE': { fr: 'Google Ads', en: 'Google Ads' },
  'ads.platform.META': { fr: 'Meta', en: 'Meta' },
  'ads.platform.DISPLAY_NETWORK': { fr: 'Bannières', en: 'Banners' },
  'ads.type.SPONSORED': { fr: 'Annonce payante', en: 'Paid ad' },
  'ads.type.ORGANIC': { fr: 'Résultat naturel', en: 'Organic result' },
  'ads.type.BANNER': { fr: 'Bannière', en: 'Banner' },

  // Statuts fiscaux
  'ads.fiscal.UNKNOWN': { fr: 'Non vérifié', en: 'Not checked' },
  'ads.fiscal.REGISTERED': { fr: 'Enregistré', en: 'Registered' },
  'ads.fiscal.NOT_REGISTERED': { fr: 'Non enregistré', en: 'Not registered' },
  'ads.fiscal.UNDER_REVIEW': { fr: 'En vérification', en: 'Being checked' },

  // Statuts de vérification
  'ads.verif.NEW': { fr: 'Nouveau', en: 'New' },
  'ads.verif.TO_REVIEW': { fr: 'À examiner', en: 'To review' },
  'ads.verif.IN_REVIEW': { fr: 'En cours', en: 'In review' },
  'ads.verif.CONFIRMED': { fr: 'Confirmé', en: 'Confirmed' },
  'ads.verif.FALSE_POSITIVE': { fr: 'Faux positif', en: 'False positive' },
  'ads.verif.ESCALATED': { fr: 'Escaladé', en: 'Escalated' },
  'ads.verif.ARCHIVED': { fr: 'Archivé', en: 'Archived' },
  'ads.verif.ARCHIVED_long': { fr: 'Archivé (dossier terminé)', en: 'Archived (case closed)' },

  // Secteurs
  'ads.cat.COMMERCE': { fr: 'Commerce', en: 'Retail' },
  'ads.cat.FINANCE': { fr: 'Finance', en: 'Finance' },
  'ads.cat.ASSURANCE': { fr: 'Assurance', en: 'Insurance' },
  'ads.cat.IMMOBILIER': { fr: 'Immobilier', en: 'Property' },
  'ads.cat.RESTAURATION': { fr: 'Restauration', en: 'Restaurants' },
  'ads.cat.EDUCATION': { fr: 'Éducation', en: 'Education' },
  'ads.cat.SANTE': { fr: 'Santé', en: 'Health' },
  'ads.cat.TRANSPORT': { fr: 'Transport', en: 'Transport' },
  'ads.cat.TELECOM': { fr: 'Télécom', en: 'Telecoms' },
  'ads.cat.TECH': { fr: 'Tech', en: 'Tech' },
  'ads.cat.MODE': { fr: 'Mode', en: 'Fashion' },
  'ads.cat.BEAUTE': { fr: 'Beauté', en: 'Beauty' },
  'ads.cat.BTP': { fr: 'BTP', en: 'Construction' },
  'ads.cat.JEUX': { fr: 'Jeux', en: 'Gambling' },
  'ads.cat.LIVRAISON': { fr: 'Livraison', en: 'Delivery' },
  'ads.cat.SERVICES': { fr: 'Services', en: 'Services' },
  'ads.cat.SOCIAL': { fr: 'Social', en: 'Social' },
  'ads.cat.CUSTOM': { fr: 'Autre', en: 'Other' },

  // Catégories de sites cibles
  'ads.target.NEWS': { fr: 'Actualités', en: 'News' },
  'ads.target.FORUM': { fr: 'Forum', en: 'Forum' },
  'ads.target.ECOMMERCE': { fr: 'E-commerce', en: 'E-commerce' },
  'ads.target.CLASSIFIEDS': { fr: 'Petites annonces', en: 'Classifieds' },
  'ads.target.BLOG': { fr: 'Blog', en: 'Blog' },
  'ads.target.PORTAL': { fr: 'Portail', en: 'Portal' },
  'ads.target.OTHER': { fr: 'Autre', en: 'Other' },

  // Annonceurs
  'ads.advertisersTitle': { fr: 'Annonceurs', en: 'Advertisers' },
  'ads.advertisersDesc': {
    fr: 'Annonceurs relevés par les scans, avec leur statut fiscal et leur vérification.',
    en: 'Advertisers collected by the scans, with their tax status and review state.',
  },
  'ads.advertisersCount': {
    fr: '{n} entrées{country} · {review} à vérifier',
    en: '{n} entries{country} · {review} to check',
  },
  'ads.exportCsv': { fr: 'Exporter en CSV', en: 'Export to CSV' },
  'ads.pickCountryToExport': { fr: 'Choisissez un pays pour exporter', en: 'Pick a country to export' },
  'ads.savedViews': { fr: 'Vues enregistrées', en: 'Saved views' },
  'ads.view.review': { fr: 'À vérifier', en: 'To check' },
  'ads.view.in_review': { fr: 'En cours', en: 'In review' },
  'ads.view.unregistered': { fr: 'Non enregistrés', en: 'Not registered' },
  'ads.view.sensitive': { fr: 'Activités sensibles', en: 'Sensitive activities' },
  'ads.view.all': { fr: 'Tous', en: 'All' },
  'ads.filters': { fr: 'Filtres', en: 'Filters' },
  'ads.searchPlaceholder': { fr: 'Nom ou domaine', en: 'Name or domain' },
  'ads.searchAria': { fr: 'Rechercher un annonceur', en: 'Search for an advertiser' },
  'ads.channel': { fr: 'Canal', en: 'Channel' },
  'ads.allChannels': { fr: 'Tous les canaux', en: 'All channels' },
  'ads.paidAndOrganic': { fr: 'Payants et naturels', en: 'Paid and organic' },
  'ads.paidAds': { fr: 'Annonces payantes', en: 'Paid ads' },
  'ads.organicResults': { fr: 'Résultats naturels', en: 'Organic results' },
  'ads.sector': { fr: 'Secteur', en: 'Sector' },
  'ads.allSectors': { fr: 'Tous les secteurs', en: 'All sectors' },
  'ads.fiscalStatus': { fr: 'Statut fiscal', en: 'Tax status' },
  'ads.allFiscalStatuses': { fr: 'Tous statuts fiscaux', en: 'All tax statuses' },
  'ads.verification': { fr: 'Vérification', en: 'Review' },
  'ads.allVerifications': { fr: 'Toutes vérifications', en: 'All review states' },
  'ads.viewVerification': { fr: 'Vérification de la vue', en: 'This view’s review state' },
  'ads.sort': { fr: 'Tri', en: 'Sort' },
  'ads.sortRecent': { fr: 'Vus récemment', en: 'Recently seen' },
  'ads.sortAdCount': { fr: "Plus d'annonces", en: 'Most ads' },
  'ads.sortSpend': { fr: 'Dépense estimée', en: 'Estimated spend' },
  'ads.sortName': { fr: 'Nom (A → Z)', en: 'Name (A → Z)' },
  'ads.clearFilters': { fr: 'Effacer les filtres', en: 'Clear filters' },
  'ads.noAdvertiserMatch': { fr: 'Aucun annonceur pour ces critères.', en: 'No advertiser matches these criteria.' },
  'ads.colAdvertiser': { fr: 'Annonceur', en: 'Advertiser' },
  'ads.colAdCount': { fr: 'Annonces', en: 'Ads' },
  'ads.colSeenOn': { fr: 'Vu le', en: 'Seen on' },
  'ads.rangeInfo': { fr: '{from}–{to} sur {total}', en: '{from}–{to} of {total}' },

  // Fiche annonceur
  'ads.sensitivePrefix': { fr: 'Activité sensible · ', en: 'Sensitive activity · ' },
  'ads.whereSeen': { fr: 'Où il a été vu', en: 'Where it was seen' },
  'ads.query': { fr: 'Requête', en: 'Query' },
  'ads.firstSeen': { fr: 'Première fois', en: 'First seen' },
  'ads.lastSeen': { fr: 'Dernière fois', en: 'Last seen' },
  'ads.estimatedSpend': { fr: 'Dépense estimée', en: 'Estimated spend' },
  'ads.adsPreview': { fr: 'Aperçu des annonces', en: 'Ad previews' },
  'ads.metaLibrary': { fr: 'Bibliothèque publicitaire Meta', en: 'Meta Ad Library' },
  'ads.openCapture': { fr: 'Ouvrir la capture en grand', en: 'Open the screenshot full size' },
  'ads.captureAlt': { fr: 'Capture de la page où {name} a été vu', en: 'Screenshot of the page where {name} was seen' },
  'ads.adAlt': { fr: 'Annonce {n}', en: 'Ad {n}' },
  'ads.viewAd': { fr: "Voir l'annonce", en: 'View the ad' },
  'ads.showLessAds': { fr: 'Afficher moins', en: 'Show fewer' },
  'ads.showAllAds': { fr: 'Afficher les {n} annonces', en: 'Show all {n} ads' },
  'ads.newStatus': { fr: 'Nouveau statut', en: 'New status' },
  'ads.agentInCharge': { fr: 'Agent en charge', en: 'Agent in charge' },
  'ads.pickAgent': { fr: 'Choisir un agent', en: 'Pick an agent' },
  'ads.commentRequired': { fr: 'Commentaire (obligatoire)', en: 'Comment (required)' },
  'ads.commentPlaceholder': {
    fr: 'Ce qui justifie le changement de statut.',
    en: 'What justifies the change of status.',
  },
  'ads.saveVerification': { fr: 'Enregistrer la vérification', en: 'Save the review' },
  'ads.noNextStep': { fr: 'Aucune étape suivante pour ce statut.', en: 'No next step for this status.' },
  'ads.inChargeOf': { fr: 'en charge : {who}', en: 'in charge: {who}' },
  'ads.fiscalNotesPlaceholder': {
    fr: "Numéro d'identification, source consultée…",
    en: 'Tax number, source consulted…',
  },
  'ads.saveFiscal': { fr: 'Enregistrer le statut fiscal', en: 'Save the tax status' },

  // Campagnes de scan
  'ads.campaignsTitle': { fr: 'Campagnes de scan', en: 'Scan campaigns' },
  'ads.campaignsDesc': {
    fr: 'Chaque scan interroge Google, Meta et les sites surveillés pour un pays.',
    en: 'Each scan queries Google, Meta and the monitored websites for one country.',
  },
  'ads.bannerAbortRequested': { fr: 'Arrêt demandé', en: 'Stop requested' },
  'ads.bannerSitesRunning': { fr: 'Passage sur les sites en cours', en: 'Website pass in progress' },
  'ads.bannerScanRunning': { fr: 'Scan en cours', en: 'Scan in progress' },
  'ads.bannerWaitSites': {
    fr: 'Annonceurs Google et Meta terminés. Les sites surveillés sont visités un par un — comptez jusqu’à une heure.',
    en: 'Google and Meta advertisers done. The monitored websites are visited one by one — allow up to an hour.',
  },
  'ads.bannerWaitAll': {
    fr: 'Interrogation de Google, Meta et des sites surveillés…',
    en: 'Querying Google, Meta and the monitored websites…',
  },
  'ads.bannerAborting': {
    fr: 'Le site en cours se termine avant l’arrêt{site}. Comptez deux à trois minutes ; les bannières déjà trouvées sont conservées.',
    en: 'The current website finishes before stopping{site}. Allow two or three minutes; banners already found are kept.',
  },
  'ads.bannerSiteStart': { fr: 'Site {index} sur {total} · {site}', en: 'Website {index} of {total} · {site}' },
  'ads.bannerSiteDone': {
    fr: 'Site {index} sur {total} · {outcome} sur {site}',
    en: 'Website {index} of {total} · {outcome} on {site}',
  },
  'ads.bannerSiteError': { fr: 'erreur', en: 'error' },
  'ads.bannerSiteBanners': { fr: ['{n} bannière', '{n} bannières'], en: ['{n} banner', '{n} banners'] },
  'ads.followScan': { fr: 'Suivre le scan', en: 'Follow the scan' },
  'ads.stopAlready': {
    fr: 'Arrêt déjà demandé : le drapeau n’est relu qu’entre deux sites',
    en: 'Stop already requested: the flag is only re-read between two websites',
  },
  'ads.stopHint': {
    fr: 'Arrête le passage sur les sites ; la recherche Google et Meta en cours se termine',
    en: 'Stops the website pass; the Google and Meta search in progress finishes',
  },
  'ads.stopping': { fr: 'Arrêt demandé…', en: 'Stopping…' },
  'ads.stop': { fr: 'Arrêter', en: 'Stop' },
  'ads.scanInterrupted': { fr: 'Scan interrompu', en: 'Scan interrupted' },
  'ads.scanFinished': { fr: 'Scan terminé', en: 'Scan complete' },
  'ads.scanResult': { fr: '{head} — {country} : {parts}', en: '{head} — {country}: {parts}' },
  'ads.scanPartAds': {
    fr: '{total} annonceurs ({google} Google, {meta} Meta), {fresh} nouveaux',
    en: '{total} advertisers ({google} Google, {meta} Meta), {fresh} new',
  },
  'ads.scanPartAdsFailed': { fr: 'annonceurs Google et Meta : échec', en: 'Google and Meta advertisers: failed' },
  'ads.scanPartBanners': {
    fr: '{banners} bannières sur {sites} sites',
    en: '{banners} banners across {sites} websites',
  },
  'ads.scanPartBannersFailed': { fr: 'bannières : échec', en: 'banners: failed' },
  'ads.scanFailed': { fr: 'Le scan a échoué : {error}', en: 'The scan failed: {error}' },
  'ads.nothingToStop': { fr: 'Aucun passage sur les sites à arrêter.', en: 'No website pass to stop.' },
  'ads.abortFailed': {
    fr: "La demande d'arrêt n'est pas passée : {error}",
    en: 'The stop request did not go through: {error}',
  },
  'ads.unknownError': { fr: 'erreur inconnue', en: 'unknown error' },
  'ads.launchBlock': { fr: 'Lancer un scan', en: 'Run a scan' },
  'ads.googleNotConfigured': {
    fr: 'Google Ads non configuré : seules Meta et les bannières seront interrogées',
    en: 'Google Ads not configured: only Meta and banners will be queried',
  },
  'ads.pickCountry': { fr: 'Choisir un pays', en: 'Pick a country' },
  'ads.internalPages': { fr: 'Pages internes par site', en: 'Internal pages per website' },
  'ads.costNote': {
    fr: "{n} requêtes actives {where} : jusqu'à {searches} recherches Google. 0 page interne = page d'accueil des sites uniquement.",
    en: '{n} active queries {where}: up to {searches} Google searches. 0 internal pages = website home pages only.',
  },
  'ads.costNoteNoCountry': {
    fr: "0 page interne = page d'accueil des sites uniquement.",
    en: '0 internal pages = website home pages only.',
  },
  'ads.clearResults': { fr: 'Effacer les résultats {where}', en: 'Clear the results {where}' },
  'ads.resetConfirm': {
    fr: 'Supprimer tous les résultats publicitaires {where} ?\n\nAnnonceurs, historique des scans et bannières relevées seront effacés. Cette action est irréversible.',
    en: 'Delete every advertising result {where}?\n\nAdvertisers, scan history and collected banners will be erased. This cannot be undone.',
  },
  'ads.resetDone': {
    fr: '{country} réinitialisé : {advertisers} annonceurs, {history} scans et {detections} bannières supprimés.',
    en: '{country} reset: {advertisers} advertisers, {history} scans and {detections} banners deleted.',
  },
  'ads.sitesRunning': { fr: 'Sites en cours…', en: 'Websites in progress…' },
  'ads.scanRunning': { fr: 'Scan en cours…', en: 'Scan in progress…' },
  'ads.quotaBlock': { fr: 'Recherches Google (SerpAPI)', en: 'Google searches (SerpAPI)' },
  'ads.quotaAdminOnly': {
    fr: 'Consommation visible par les administrateurs uniquement.',
    en: 'Consumption visible to administrators only.',
  },
  'ads.serpapiOff': {
    fr: "SerpAPI n'est pas configuré : aucune recherche Google n'est faite.",
    en: 'SerpAPI is not configured: no Google search is run.',
  },
  'ads.quotaSinceBoot': {
    fr: 'recherches depuis le dernier démarrage du serveur',
    en: 'searches since the server last started',
  },
  'ads.quotaExhausted': { fr: 'Quota épuisé', en: 'Quota exhausted' },
  'ads.quotaExhaustedSince': { fr: 'Quota épuisé depuis le {date}', en: 'Quota exhausted since {date}' },
  'ads.quotaAvailable': { fr: 'Recherches disponibles', en: 'Searches available' },
  'ads.quotaMonthlyNote': {
    fr: 'Le décompte mensuel du forfait se consulte sur le tableau de bord SerpAPI.',
    en: 'The plan’s monthly count is on the SerpAPI dashboard.',
  },
  'ads.logBlock': { fr: 'Journal du dernier scan', en: 'Last scan log' },
  'ads.logSummary': {
    fr: '{n} entrées · {errors} erreurs · {warnings} avertissements',
    en: '{n} entries · {errors} errors · {warnings} warnings',
  },
  'ads.logLevelOk': { fr: 'OK', en: 'OK' },
  'ads.logLevelWarn': { fr: 'Alerte', en: 'Warning' },
  'ads.logLevelError': { fr: 'Erreur', en: 'Error' },
  'ads.trendTitle': { fr: 'Nouveaux annonceurs par jour de scan', en: 'New advertisers per scan day' },
  'ads.trendTitleCountry': {
    fr: 'Nouveaux annonceurs par jour de scan · {country}',
    en: 'New advertisers per scan day · {country}',
  },
  'ads.trendSub': { fr: '90 derniers jours', en: 'Last 90 days' },
  'ads.trendEmpty': {
    fr: "Pas encore d'historique : lancez un scan pour alimenter la tendance.",
    en: 'No history yet: run a scan to start the trend.',
  },
  'ads.trendAria': {
    fr: 'Nouveaux annonceurs trouvés à chaque scan',
    en: 'New advertisers found at each scan',
  },
  'ads.trendPoint': {
    fr: '{date} · {country} : {fresh} nouveaux, {total} trouvés',
    en: '{date} · {country}: {fresh} new, {total} found',
  },
  'ads.historyTitle': { fr: 'Historique des scans d’annonceurs', en: 'Advertiser scan history' },
  'ads.historySub': { fr: ['{n} scan · hors passage sur les sites', '{n} scans · hors passage sur les sites'], en: ['{n} scan · website pass excluded', '{n} scans · website pass excluded'] },
  'ads.historyEmpty': { fr: 'Aucun scan enregistré.', en: 'No scan recorded.' },
  'ads.colBanners': { fr: 'Bannières', en: 'Banners' },
  'ads.colNew': { fr: 'Nouveaux', en: 'New' },
  'ads.colDuration': { fr: 'Durée', en: 'Duration' },
  'ads.colRemark': { fr: 'Remarque', en: 'Remark' },
  'ads.statusSuccess': { fr: 'Terminé', en: 'Complete' },
  'ads.statusPartial': { fr: 'Partiel', en: 'Partial' },
  'ads.statusFailed': { fr: 'Échec', en: 'Failed' },
  'ads.seeLog': { fr: 'Voir le journal', en: 'See the log' },
  'ads.noLogForScan': { fr: 'Aucun journal pour ce scan.', en: 'No log for this scan.' },

  // Bannières
  'ads.bannersTitle': { fr: 'Bannières sur les sites', en: 'Banners on websites' },
  'ads.bannersDesc': {
    fr: "Publicités affichées sur les sites d'actualité, forums et petites annonces les plus visités.",
    en: 'Ads displayed on the most visited news, forum and classifieds websites.',
  },
  'ads.addSite': { fr: 'Ajouter un site', en: 'Add a website' },
  'ads.scanSites': { fr: 'Scanner les sites', en: 'Scan the websites' },
  'ads.kpiMonitoredSites': { fr: 'Sites surveillés', en: 'Monitored websites' },
  'ads.kpiTotalSites': { fr: '{n} au total', en: '{n} in total' },
  'ads.kpiBanners': { fr: 'Bannières relevées', en: 'Banners collected' },
  'ads.kpiNetworks': { fr: 'Réseaux publicitaires', en: 'Ad networks' },
  'ads.kpiStale': { fr: 'Sites non scannés depuis 7 jours', en: 'Websites not scanned for 7 days' },
  'ads.display': { fr: 'Affichage', en: 'Display' },
  'ads.tabOverview': { fr: "Vue d'ensemble", en: 'Overview' },
  'ads.tabAllBanners': { fr: 'Toutes les bannières', en: 'All banners' },
  'ads.tabSites': { fr: 'Sites surveillés', en: 'Monitored websites' },
  'ads.latestBanners': { fr: 'Dernières bannières détectées', en: 'Latest banners detected' },
  'ads.allBannersLink': { fr: 'Toutes les bannières', en: 'All banners' },
  'ads.noBannerYet': { fr: 'Aucune bannière relevée. Lancez un scan des sites.', en: 'No banner collected. Run a website scan.' },
  'ads.directSale': { fr: 'Vente directe', en: 'Direct sale' },
  'ads.topsTitle': { fr: 'Annonceurs et sites les plus présents', en: 'Most present advertisers and websites' },
  'ads.topsEmpty': {
    fr: 'Les classements apparaîtront après le premier scan.',
    en: 'The rankings appear after the first scan.',
  },
  'ads.topSites': { fr: 'Sites', en: 'Websites' },
  'ads.topAdvertiserCount': {
    fr: ['{n} sur {sites} site', '{n} sur {sites} sites'],
    en: ['{n} on {sites} website', '{n} on {sites} websites'],
  },
  'ads.topSiteCount': { fr: '{n} pubs · {uniq} annonceurs', en: '{n} ads · {uniq} advertisers' },
  'ads.filterAdvertiser': { fr: 'Annonceur', en: 'Advertiser' },
  'ads.filterAdvertiserAria': { fr: 'Filtrer par annonceur', en: 'Filter by advertiser' },
  'ads.filterSite': { fr: 'Site', en: 'Website' },
  'ads.filterSiteAria': { fr: 'Filtrer par site', en: 'Filter by website' },
  'ads.bannerCount': { fr: ['{n} bannière', '{n} bannières'], en: ['{n} banner', '{n} banners'] },
  'ads.noBannerMatch': { fr: 'Aucune bannière pour ces critères.', en: 'No banner matches these criteria.' },
  'ads.colNetwork': { fr: 'Réseau', en: 'Network' },
  'ads.colFormat': { fr: 'Format', en: 'Format' },
  'ads.colAnalysis': { fr: 'Analyse', en: 'Analysis' },
  'ads.visionNotAnalysed': { fr: 'Non analysée', en: 'Not analysed' },
  'ads.visionBlock': { fr: 'À bloquer', en: 'To block' },
  'ads.visionFlag': { fr: 'À examiner', en: 'To review' },
  'ads.visionClear': { fr: 'Sans alerte', en: 'No alert' },
  'ads.bannerAlt': { fr: 'Bannière de {name} sur {site}', en: 'Banner from {name} on {site}' },
  'ads.distribution': { fr: 'Diffusion', en: 'Distribution' },
  'ads.destination': { fr: 'Destination', en: 'Destination' },
  'ads.imageAnalysis': { fr: "Analyse de l'image", en: 'Image analysis' },
  'ads.visionAdvertiser': { fr: 'Annonceur lu', en: 'Advertiser read' },
  'ads.visionConfidence': { fr: 'Confiance', en: 'Confidence' },
  'ads.visionPaymentMethods': { fr: 'Moyens de paiement', en: 'Payment methods' },
  'ads.visionProhibited': { fr: 'Contenu interdit : {detail}', en: 'Prohibited content: {detail}' },
  'ads.technicalDetail': { fr: 'Détail technique', en: 'Technical detail' },
  'ads.adSlot': { fr: 'Emplacement publicitaire', en: 'Ad slot' },
  'ads.selector': { fr: 'Sélecteur', en: 'Selector' },
  'ads.fullUrl': { fr: 'Adresse complète', en: 'Full address' },
  'ads.sitesBlock': { fr: 'Sites surveillés', en: 'Monitored websites' },
  'ads.sitesBlockRecent': { fr: 'Scannés le plus récemment', en: 'Most recently scanned' },
  'ads.sitesBlockCsv': {
    fr: 'Fichier CSV : colonnes url et pays obligatoires, nom et catégorie facultatives',
    en: 'CSV file: url and country columns required, name and category optional',
  },
  'ads.allSitesLink': { fr: 'Les {n} sites', en: 'All {n} websites' },
  'ads.importCsv': { fr: 'Importer un CSV', en: 'Import a CSV' },
  'ads.importResultSites': { fr: ['{n} site importé', '{n} sites importés'], en: ['{n} website imported', '{n} websites imported'] },
  'ads.importResultSkipped': { fr: ['{n} doublon ignoré', '{n} doublons ignorés'], en: ['{n} duplicate skipped', '{n} duplicates skipped'] },
  'ads.importRowError': { fr: 'Ligne {row}{url} : {reason}', en: 'Row {row}{url}: {reason}' },
  'ads.emptyCsv': { fr: 'Fichier vide ou sans ligne de données.', en: 'Empty file, or no data row.' },
  'ads.csvNoUrl': { fr: 'Colonne « url » introuvable dans l’en-tête.', en: 'Column “url” not found in the header.' },
  'ads.csvNoCountry': {
    fr: 'Colonne « country » ou « pays » introuvable dans l’en-tête.',
    en: 'Column “country” not found in the header.',
  },
  'ads.noSiteMonitored': {
    fr: 'Aucun site surveillé. Ajoutez des sites ou importez une liste.',
    en: 'No website monitored. Add websites or import a list.',
  },
  'ads.colNetworksFound': { fr: 'Réseaux détectés', en: 'Networks detected' },
  'ads.colLastScan': { fr: 'Dernier scan', en: 'Last scan' },
  'ads.siteSuspend': { fr: 'Suspendre', en: 'Suspend' },
  'ads.siteReactivate': { fr: 'Réactiver', en: 'Reactivate' },
  'ads.siteRemove': { fr: 'Retirer', en: 'Remove' },
  'ads.siteRemoveConfirm': {
    fr: 'Retirer {name} des sites surveillés ?',
    en: 'Remove {name} from the monitored websites?',
  },
  'ads.addSiteTitle': { fr: 'Ajouter un site à surveiller', en: 'Add a website to monitor' },
  'ads.addSiteSub': {
    fr: 'Le robot relève les bannières publicitaires de ce site à chaque scan du pays.',
    en: 'The crawler collects this website’s banners at every country scan.',
  },
  'ads.addSiteSubmit': { fr: 'Ajouter le site', en: 'Add the website' },
  'ads.adding': { fr: 'Ajout…', en: 'Adding…' },
  'ads.address': { fr: 'Adresse', en: 'Address' },
  'ads.siteNamePlaceholder': { fr: 'Ex. : Seneweb', en: 'E.g. Seneweb' },
  'ads.pick': { fr: 'Choisir', en: 'Pick' },

  // Requêtes surveillées
  'ads.queriesTitle': { fr: 'Requêtes surveillées', en: 'Monitored queries' },
  'ads.queriesDesc': {
    fr: "Les recherches Google jouées à chaque scan, comme un internaute du pays. Chaque requête active consomme jusqu'à {pages} recherches du quota par pays scanné.",
    en: 'The Google searches run at each scan, as a local user would. Each active query uses up to {pages} searches of the quota per scanned country.',
  },
  'ads.searchQueryPlaceholder': { fr: 'Rechercher une requête', en: 'Search for a query' },
  'ads.playedIn': { fr: 'Jouées {where}', en: 'Run {where}' },
  'ads.allCountriesPlayed': { fr: 'Tous les pays', en: 'All countries' },
  'ads.queryCount': { fr: ['{n} requête', '{n} requêtes'], en: ['{n} query', '{n} queries'] },
  'ads.noQueryMatch': { fr: 'Aucune requête ne correspond à cette recherche.', en: 'No query matches this search.' },
  'ads.noQueryConfigured': {
    fr: 'Aucune requête configurée pour ce périmètre.',
    en: 'No query configured for this scope.',
  },
  'ads.activeOutOf': { fr: ['{n} active sur {total}', '{n} actives sur {total}'], en: ['{n} active of {total}', '{n} active of {total}'] },
  'ads.scanCostTitle': { fr: "Coût d'un scan", en: 'Cost of a scan' },
  'ads.scanCostTitleCountry': { fr: "Coût d'un scan {where}", en: 'Cost of a scan {where}' },
  'ads.atMostSearches': { fr: 'recherches Google au plus', en: 'Google searches at most' },
  'ads.scanCostNote': {
    fr: '{n} requêtes actives × {pages} pages de résultats. Réserver une requête à certains pays réduit ce coût partout ailleurs.',
    en: '{n} active queries × {pages} result pages. Restricting a query to some countries lowers this cost everywhere else.',
  },
  'ads.disableQuery': { fr: 'Désactiver « {query} »', en: 'Disable “{query}”' },
  'ads.enableQuery': { fr: 'Activer « {query} »', en: 'Enable “{query}”' },
  'ads.searchLabel': { fr: 'Recherche', en: 'Search' },
  'ads.queryOnlyIn': { fr: 'Jouée uniquement dans ces pays', en: 'Run only in these countries' },
  'ads.queryEverywhere': { fr: 'Jouée dans tous les pays scannés', en: 'Run in every scanned country' },
  'ads.allCountriesShort': { fr: 'Tous pays', en: 'All countries' },
  'ads.deleteQueryConfirm': { fr: 'Supprimer la requête « {query} » ?', en: 'Delete the query “{query}”?' },
  'ads.addQueryTitle': { fr: 'Ajouter une requête', en: 'Add a query' },
  'ads.queryPlaceholder': { fr: 'Ex. : livraison express', en: 'E.g. express delivery' },
  'ads.addQuerySubmit': { fr: 'Ajouter la requête', en: 'Add the query' },
  'ads.addQueryDone': { fr: 'Requête « {query} » ajoutée.', en: 'Query “{query}” added.' },
  'ads.pickerHint': {
    fr: 'Pays — sans sélection, la requête est jouée dans tous les pays scannés',
    en: 'Countries — with none selected, the query runs in every scanned country',
  },
  'ads.pickerAria': { fr: 'Pays où jouer la requête', en: 'Countries where the query runs' },

  // Durées
  'ads.durationMs': { fr: '{n} ms', en: '{n} ms' },
  'ads.durationS': { fr: '{n} s', en: '{n} s' },
  'ads.durationMin': { fr: '{m} min {s}', en: '{m} min {s}' },
} satisfies Bundle
